import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { HelpCircle, Plus, Pencil, Trash2 } from "lucide-react";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { ImportButtons } from "@/components/admin/import/ImportButtons";
import { AdminSection } from "@/components/admin/AdminSection";
import { RichTextEditor } from "@/components/admin/cms/RichTextEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  listFaqs,
  saveFaqCategory,
  deleteFaqCategory,
  saveFaq,
  deleteFaq,
} from "@/lib/admin/cms.functions";

export const Route = createFileRoute("/_admin/admin/cms/faq")({
  component: FaqAdminPage,
});

type Category = {
  id: string;
  name: string;
  slug: string;
  display_order: number;
  is_published: boolean;
  is_demo: boolean;
};
type Faq = {
  id: string;
  category_id: string | null;
  question: string;
  answer_html: string | null;
  keywords: string | null;
  display_order: number;
  is_published: boolean;
  is_demo: boolean;
};

type CatDraft = {
  id?: string;
  name: string;
  slug: string;
  display_order: number;
  is_published: boolean;
  is_demo: boolean;
};
type FaqDraft = {
  id?: string;
  category_id: string;
  question: string;
  answer_html: string;
  keywords: string;
  display_order: number;
  is_published: boolean;
  is_demo: boolean;
};

const slugify = (v: string) =>
  v.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

function FaqAdminPage() {
  const list = useServerFn(listFaqs);
  const saveCat = useServerFn(saveFaqCategory);
  const delCat = useServerFn(deleteFaqCategory);
  const saveQ = useServerFn(saveFaq);
  const delQ = useServerFn(deleteFaq);
  const qc = useQueryClient();

  const [catDraft, setCatDraft] = useState<CatDraft | null>(null);
  const [faqDraft, setFaqDraft] = useState<FaqDraft | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ["cms-admin", "faq"], queryFn: () => list() });
  const categories = useMemo(
    () => ((data?.categories ?? []) as unknown as Category[]).sort((a, b) => a.display_order - b.display_order),
    [data],
  );
  const faqs = useMemo(() => (data?.faqs ?? []) as unknown as Faq[], [data]);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["cms-admin", "faq"] });
    void qc.invalidateQueries({ queryKey: ["cms", "faqs"] });
  };

  const catMutation = useMutation({
    mutationFn: (d: CatDraft) => saveCat({ data: { ...d, slug: d.slug || slugify(d.name) } }),
    onSuccess: () => { toast.success("Category saved"); setCatDraft(null); refresh(); },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Save failed"),
  });
  const faqMutation = useMutation({
    mutationFn: (d: FaqDraft) => saveQ({ data: d }),
    onSuccess: () => { toast.success("Question saved"); setFaqDraft(null); refresh(); },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Save failed"),
  });
  const delCatMutation = useMutation({
    mutationFn: (id: string) => delCat({ data: { id } }),
    onSuccess: () => { toast.success("Category removed"); refresh(); },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Couldn't delete the category."),
  });
  const delFaqMutation = useMutation({
    mutationFn: (id: string) => delQ({ data: { id } }),
    onSuccess: () => { toast.success("Question removed"); refresh(); },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Couldn't delete the question."),
  });

  return (
    <div className="space-y-4">
      <AdminPageHeader
        title="FAQ"
        description="Questions grouped by category, published to the public /faq page."
        actions={
          <ImportButtons
            moduleId="faq"
            showExport
            exportName="FAQs"
            exportRows={() =>
              faqs.map((f) => ({
                Category: categories.find((c) => c.id === f.category_id)?.name ?? "",
                Question: f.question,
                Answer: f.answer_html?.replace(/<[^>]*>?/gm, "") ?? "",
                Keywords: f.keywords ?? "",
                Status: f.is_published ? "Active" : "Inactive",
                "Sort Order": f.display_order,
              }))
            }
          />
        }
      />
      <div className="flex justify-end">
        <Button
          size="sm"
          className="gap-1"
          onClick={() => setCatDraft({ name: "", slug: "", display_order: categories.length, is_published: true, is_demo: false })}
        >
          <Plus className="h-3.5 w-3.5" aria-hidden /> Add category
        </Button>
      </div>

      {isLoading ? <p className="text-sm text-muted-foreground">Loading FAQs…</p> : null}

      {categories.map((cat, i) => {
        const items = faqs
          .filter((f) => f.category_id === cat.id)
          .sort((a, b) => a.display_order - b.display_order);
        return (
          <AdminSection
            key={cat.id}
            number={i + 1}
            icon={HelpCircle}
            title={cat.name}
            description={`${items.length} question${items.length === 1 ? "" : "s"}${cat.is_published ? "" : " · unpublished"}`}
            customised={items.length > 0}
            actions={
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1"
                  onClick={() =>
                    setFaqDraft({
                      category_id: cat.id,
                      question: "",
                      answer_html: "",
                      keywords: "",
                      display_order: items.length,
                      is_published: true,
                      is_demo: false,
                    })
                  }
                >
                  <Plus className="h-3.5 w-3.5" aria-hidden /> Question
                </Button>
                <Button size="icon" variant="ghost" onClick={() => setCatDraft({ ...cat })}>
                  <Pencil className="h-4 w-4" aria-hidden />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="icon" variant="ghost">
                      <Trash2 className="h-4 w-4 text-destructive" aria-hidden />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete this category?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This removes "{cat.name}" and all its questions permanently. This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => delCatMutation.mutate(cat.id)}>
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            }
          >
            {items.length === 0 ? (
              <p className="text-sm text-muted-foreground">No questions in this category.</p>
            ) : (
              <ul className="divide-y rounded-md border">
                {items.map((f) => (
                  <li key={f.id} className="flex flex-wrap items-center gap-2 px-3 py-2">
                    <span className="min-w-48 flex-1 text-sm text-foreground">{f.question}</span>
                    {!f.is_published ? (
                      <span className="rounded border px-1.5 text-[10px] uppercase text-muted-foreground">Hidden</span>
                    ) : null}
                    <div className="ml-auto flex gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() =>
                          setFaqDraft({
                            id: f.id,
                            category_id: f.category_id ?? cat.id,
                            question: f.question,
                            answer_html: f.answer_html ?? "",
                            keywords: f.keywords ?? "",
                            display_order: f.display_order,
                            is_published: f.is_published,
                            is_demo: f.is_demo,
                          })
                        }
                      >
                        <Pencil className="h-4 w-4" aria-hidden />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="icon" variant="ghost">
                            <Trash2 className="h-4 w-4 text-destructive" aria-hidden />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete this question?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This removes "{f.question}" permanently. This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => delFaqMutation.mutate(f.id)}>
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </AdminSection>
        );
      })}

      <Dialog open={catDraft !== null} onOpenChange={(o) => !o && setCatDraft(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{catDraft?.id ? "Edit category" : "Add category"}</DialogTitle>
          </DialogHeader>
          {catDraft ? (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="cat-name" className="text-xs">Name</Label>
                <Input id="cat-name" value={catDraft.name} onChange={(e) => setCatDraft({ ...catDraft, name: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cat-slug" className="text-xs">Slug</Label>
                <Input id="cat-slug" value={catDraft.slug} onChange={(e) => setCatDraft({ ...catDraft, slug: e.target.value })} placeholder="ordering" />
              </div>
              <div className="flex items-center justify-between rounded-md border p-3">
                <span className="text-sm">Published</span>
                <Switch checked={catDraft.is_published} onCheckedChange={(v) => setCatDraft({ ...catDraft, is_published: v })} />
              </div>
              <div className="flex items-center justify-between rounded-md border p-3">
                <span className="text-sm">Demo Data</span>
                <Switch checked={catDraft.is_demo} onCheckedChange={(v) => setCatDraft({ ...catDraft, is_demo: v })} />
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCatDraft(null)}>Cancel</Button>
            <Button disabled={!catDraft?.name.trim() || catMutation.isPending} onClick={() => catDraft && catMutation.mutate(catDraft)}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={faqDraft !== null} onOpenChange={(o) => !o && setFaqDraft(null)}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{faqDraft?.id ? "Edit question" : "Add question"}</DialogTitle>
          </DialogHeader>
          {faqDraft ? (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="faq-q" className="text-xs">Question</Label>
                <Input id="faq-q" value={faqDraft.question} onChange={(e) => setFaqDraft({ ...faqDraft, question: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="faq-kw" className="text-xs">Keywords</Label>
                <Input id="faq-kw" value={faqDraft.keywords} onChange={(e) => setFaqDraft({ ...faqDraft, keywords: e.target.value })} placeholder="comma, separated, tags" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Answer</Label>
                <RichTextEditor value={faqDraft.answer_html} onChange={(html) => setFaqDraft({ ...faqDraft, answer_html: html })} />
              </div>
              <div className="flex items-center justify-between rounded-md border p-3">
                <span className="text-sm">Published</span>
                <Switch checked={faqDraft.is_published} onCheckedChange={(v) => setFaqDraft({ ...faqDraft, is_published: v })} />
              </div>
              <div className="flex items-center justify-between rounded-md border p-3">
                <span className="text-sm">Demo Data</span>
                <Switch checked={faqDraft.is_demo} onCheckedChange={(v) => setFaqDraft({ ...faqDraft, is_demo: v })} />
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setFaqDraft(null)}>Cancel</Button>
            <Button disabled={!faqDraft?.question.trim() || faqMutation.isPending} onClick={() => faqDraft && faqMutation.mutate(faqDraft)}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
